// Unified import hub — see history + start a new import for any resource kind.
import React, { useEffect, useState } from "react";
import { api, asList } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { format } from "date-fns";
import { FileSpreadsheet, RefreshCw, ArrowRight, CheckCircle2, AlertTriangle, Loader2, Package, Users, Factory, UserCog, Wrench, Store } from "lucide-react";
import BulkImporter from "@/components/BulkImporter";

const KINDS = [
  { key: "products",    label: "Products / Catalog",  icon: Package },
  { key: "contacts",    label: "Contacts / Customers", icon: Users },
  { key: "assets",      label: "Factory Assets",       icon: Factory },
  { key: "staff",       label: "Staff / HR",           icon: UserCog },
  { key: "consumables", label: "Consumables",          icon: Wrench },
  { key: "vendors",     label: "Vendors",              icon: Store },
];

export default function ImportHubPage() {
  const [kind, setKind] = useState("products");
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showImporter, setShowImporter] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/import/jobs", { params: { limit: 50 } });
      setJobs(asList(data));
    } catch (err) { void err; toast.error("Failed to load import history"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = kind === "all" ? jobs : jobs.filter(j => j.kind === kind);

  return (
    <div className="space-y-6" data-testid="import-hub-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-[#4A5A50]">Data</div>
          <h1 className="mt-1 font-serif text-3xl md:text-4xl text-[#0F1E15]">Import hub</h1>
          <p className="text-sm text-[#4A5A50] mt-1">Bulk-load products, contacts, assets, staff, consumables and vendors from spreadsheets.</p>
        </div>
        <Button variant="outline" onClick={load} className="rounded-full h-11" data-testid="import-refresh"><RefreshCw size={14} className="mr-1"/> Refresh</Button>
      </div>

      {/* Kind tiles */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {KINDS.map(k => (
          <button
            key={k.key}
            onClick={() => { setKind(k.key); setShowImporter(true); }}
            className={`border rounded-xl p-4 text-left hover:shadow-md transition ${kind === k.key ? "border-[#0F5E39] bg-[#E1F0E6]/30" : "border-[#E4DFCF] bg-white"}`}
            data-testid={`import-tile-${k.key}`}
          >
            <k.icon size={20} className={kind === k.key ? "text-[#0F5E39]" : "text-[#4A5A50]"}/>
            <div className="mt-2 font-medium text-sm">{k.label}</div>
            <div className="mt-1 text-[10px] uppercase tracking-widest text-[#4A5A50] inline-flex items-center gap-1">Import<ArrowRight size={10}/></div>
          </button>
        ))}
      </div>

      {showImporter && (
        <Card className="border-[#E4DFCF] rounded-2xl">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="font-serif text-xl">New import · {KINDS.find(k => k.key === kind)?.label}</div>
              <button onClick={() => { setShowImporter(false); load(); }} className="text-xs text-[#4A5A50] hover:text-[#0F1E15]">Close</button>
            </div>
            <BulkImporter kind={kind} quicksellFirst={kind === "products"} onDone={() => { setShowImporter(false); load(); }}/>
          </CardContent>
        </Card>
      )}

      {/* History */}
      <div className="flex items-center gap-2 mt-6">
        <div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">History</div>
        <Select value={kind} onValueChange={setKind}>
          <SelectTrigger className="w-56" data-testid="import-history-filter"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All kinds</SelectItem>
            {KINDS.map(k => <SelectItem key={k.key} value={k.key}>{k.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {loading ? (
        <div className="text-sm text-[#4A5A50] inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin"/> Loading…</div>
      ) : filtered.length === 0 ? (
        <Card className="border-dashed border-[#E4DFCF] rounded-xl"><CardContent className="p-8 text-center text-sm text-[#4A5A50]">
          <FileSpreadsheet className="mx-auto mb-2 text-[#0F5E39]" size={32}/>
          No imports yet. Pick a resource above to start.
        </CardContent></Card>
      ) : (
        <Card className="border-[#E4DFCF] rounded-2xl"><CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-widest text-[#4A5A50] bg-[#F1EDDF]/40"><tr>
                <th className="text-left p-3">When</th>
                <th className="text-left p-3">Kind</th>
                <th className="text-left p-3">File</th>
                <th className="text-left p-3">Status</th>
                <th className="text-right p-3">Rows</th>
                <th className="text-right p-3">Inserted · Updated · Skipped</th>
                <th className="text-right p-3">By</th>
              </tr></thead>
              <tbody>
                {filtered.map((j, i) => (
                  <tr key={j.id} className={i > 0 ? "border-t border-[#E4DFCF]" : ""} data-testid={`import-history-row-${j.id}`}>
                    <td className="p-3 text-[#4A5A50]">{j.created_at ? format(new Date(j.created_at), "dd MMM · HH:mm") : "—"}</td>
                    <td className="p-3">{j.kind}{j.options?.format === "quicksell" ? <span className="ml-1 text-[10px] uppercase tracking-widest bg-[#F6EED8] text-[#7A6329] px-1.5 py-0.5 rounded-full">QuickSell</span> : null}</td>
                    <td className="p-3 truncate max-w-[220px]" title={j.filename}>{j.filename}</td>
                    <td className="p-3"><StatusPill status={j.status}/></td>
                    <td className="p-3 text-right">{j.processed || 0} / {j.total_rows || 0}</td>
                    <td className="p-3 text-right">
                      <span className="text-[#0F5E39]">{j.inserted || 0}</span>
                      <span className="mx-1 text-[#4A5A50]">·</span>
                      <span className="text-[#7A6329]">{j.updated || 0}</span>
                      <span className="mx-1 text-[#4A5A50]">·</span>
                      <span className={j.skipped ? "text-amber-700" : "text-[#4A5A50]"}>{j.skipped || 0}</span>
                    </td>
                    <td className="p-3 text-right text-[#4A5A50]">{j.created_by_name || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent></Card>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    completed: { cls: "bg-[#E1F0E6] text-[#0F5E39]", icon: CheckCircle2 },
    processing: { cls: "bg-[#FCF7EA] text-[#7A6329]", icon: Loader2 },
    queued: { cls: "bg-[#F1EDDF] text-[#4A5A50]", icon: Loader2 },
    partial: { cls: "bg-amber-50 text-amber-800", icon: AlertTriangle },
    failed: { cls: "bg-red-50 text-red-700", icon: AlertTriangle },
  };
  const m = map[status] || map.queued;
  const Icon = m.icon;
  return <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${m.cls}`}><Icon size={10} className={status === "processing" ? "animate-spin" : ""}/> {status}</span>;
}
